import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    CatalogItemRow,
    CatalogItemsTableCallbacks,
    CatalogItemsViewOptions,
} from "../catalogItems.types";

export const ActionsCell = memo(function ActionsCell({
    row,
    table,
}: CellContext<ViewTableFeatures, CatalogItemRow, unknown>) {
    const { callbacks } = useViewTableMeta<
        CatalogItemsTableCallbacks,
        CatalogItemsViewOptions
    >(table);

    const item = row.original;
    if (item.isNew) {
        return <ViewTableCell />;
    }

    // Items referenced by at least one invoice can't be deleted (FK
    // constraint on the backend) — disable Delete up front with an
    // explanatory tooltip instead of letting the user hit the 409 toast.
    const isReferencedByInvoices = (item.invoiceCount ?? 0) > 0;

    return (
        <ViewTableCell captureClicks>
            <Menu.More className={viewTableStyles.showOnHover}>
                <Menu.Item onClick={() => callbacks?.onEdit(item)}>
                    Edit
                </Menu.Item>
                <Menu.Item onClick={() => callbacks?.onDuplicate(item)}>
                    Duplicate
                </Menu.Item>
                <Menu.Item onClick={() => callbacks?.onArchiveToggle(item)}>
                    {item.active ? "Archive" : "Unarchive"}
                </Menu.Item>
                <Menu.Item
                    destructive
                    disabled={isReferencedByInvoices}
                    disabledReason="Item is referenced by one or more invoices and can't be deleted; archive it instead"
                    onClick={() => callbacks?.onDelete(item)}
                >
                    Delete
                </Menu.Item>
            </Menu.More>
        </ViewTableCell>
    );
});
